/* eslint-disable consistent-return, no-nested-ternary, no-else-return */

import { ButtonWithLink } from '@gameday/react-components/components'
import AppliedDiscounts from '@gameday/react-components/components/AppliedDiscounts'
import FlashMessage from '@gameday/react-components/components/FlashMessage'
import { Modal } from '@gameday/react-components/components/Modal'
import PaymentSummary from '@gameday/react-components/components/PaymentSummary'
import PromoCode from '@gameday/react-components/components/PromoCode'
import RemoveWrapper from '@gameday/react-components/components/RemoveWrapper'
import VoucherCode from '@gameday/react-components/components/VoucherCode'
import theme, { rem } from '@gameday/react-components/utils/theme'
import { filter, isEmpty, isEqual, isNil, uniq, xor } from 'lodash'
import { useEffect, useState } from 'react'
import scrollToElement from 'scroll-to-element'
import styled from 'styled-components'

import { mapSubEvents } from '../../../../factories/filter'
import useInsurance from '../../Common/Insurance/hooks/useInsurance'
import { InsuranceBody, scrollToInsuranceWidget } from '../../Common/Insurance/InsuranceBody'
import NavigationButtons from '../../Common/NavigationButton'
import PaymentGateway from '../../Common/PaymentGateway'
import styles from './styles.scss'
import useDiscounts from './useDiscounts'
import useOrderItems from './useOrderItems'
import usePromoCodes from './usePromoCodes'
import useRegistrationData from './useRegistrationData'
import useVouchers from './useVouchers'

const initialModalState = {
  label: '',
  showModal: false,
  description: '',
  onModalSubmit: () => {},
}

const StyledRegisterAnother = styled.div`
  padding: ${rem(0)} 0 ${rem(18)};
  > button {
    width: 100%;
  }
`

const ErrorMessage = styled.p`
  color: ${theme.colors.red};
  margin-bottom: 0;
`

const StyledSummary = styled.div`
  .MuiButton-textPrimary {
    color: ${props => props.buttonColour};
  }
  .input-icon:hover {
    color: ${props => props.buttonColour} !important;
  }
`

const StyledVoucher = styled.div`
  .MuiButton-containedPrimary,
  .MuiButton-containedPrimary:disabled {
    background-color: ${props => props.buttonColour};
  }
  .Mui-focused .MuiInput-input,
  .bordered .custom-textfield:focus-within {
    border-color: ${props => props.buttonColour};
  }
`

const StyledModal = styled.div`
  .MuiButton-containedPrimary {
    background-color: ${props => props.buttonColour};
  }
`

const StyledPromoCode = styled.div`
  .custom-textfield:focus-within {
    border-color: ${props => props.buttonColour} !important;
  }
`

const StyledInsuranceBody = styled.div`
  margin-bottom: ${rem(30)};

  & > .insurance-widget {
    outline: 1px solid transparent;
    outline-color: ${props => (props.isError ? theme.colors.red : 'transparent')};
  }

  ${ErrorMessage} {
    margin-bottom: ${rem(16)};
  }
`

const SummaryComponent = ({
  buttonColour,
  buttonTextColour,
  textLinkColour,
  next,
  previous,
  goto,
  organisationsInfo,
  formData,
  removeMember,
  removeMemberItems,
  setCurrentRegistration,
  validateSessions,
  summaryPageError,
  onSummaryPageError,
  reset,
  resetTimer,
  startTimer,
  setStartTimer,
  formType,
  voucherSessionId,
  isVoucherEnabledForm,
  setIsVoucherEnabledForm,
  initVoucherSession,
  setPaymentMethod,
  paymentGateway,
  feeConfigurations,
  voucherDataOnLoad,
  validateVoucherBeforeProceed,
  validateVoucher,
  removeVoucher,
  hasSubEvents,
  successPage,
  initPromoCodeSession,
  promoCodeValue,
  promoCodeError,
  addPromoCode,
  removePromoCode,
  validatedPromoCodes,
  isTeamEvent,
  teamFormType,
  removeTeam,
  removeTeamItems,
  formSettings,
  displayPriceAsZero = false,
  onRegisterAnother,
  sessionId,
}) => {
  const [paymentProvider, setPaymentProvider] = useState(null)
  const [useInstalments, setUseInstalments] = useState(false)
  const [modalState, onModalStateChange] = useState(initialModalState)
  const [insuranceError, setInsuranceError] = useState(false)

  const isTeamRego = isTeamEvent && teamFormType === 'Register A Team'

  useEffect(() => {
    if (!isTeamRego && Object.keys(formData.registrations).length < 1) {
      reset()
    }
  }, [formData])

  const { appliedDiscounts, appliedBundleDiscounts, getProductDiscount } = useDiscounts(formData)

  const { appliedVouchers, appliedVoucherCodes, hasAppliedVoucher, applyVoucher, getShowVouchers } = useVouchers({
    voucherData: voucherDataOnLoad,
    formData,
    formType,
    organisationsInfo,
    validateVoucher,
    initVoucherSession,
  })

  const { calculateProductPromoCode } = usePromoCodes(formData, validatedPromoCodes, initPromoCodeSession)

  const orderItems = useOrderItems(
    appliedVouchers,
    organisationsInfo,
    formData,
    getProductDiscount,
    calculateProductPromoCode
  )

  const { isInsuranceEnabled, insuranceProvider, insuranceQuote } = useInsurance({
    orderItems,
    formSettings,
    sessionId,
  })

  const {
    childProductMap,
    registrations,
    paidProductsByMember,
    tfcError,
    sessions,
    summaries,
    itemsGroupMap,
    paymentData,
    registeredTeam,
    instalments,
  } = useRegistrationData({
    formData,
    formType,
    orderItems,
    feeConfigurations,
    paymentProvider,
    paymentGateway,
    appliedVouchers,
    appliedDiscounts,
    useInstalments,
    formSettings,
    displayPriceAsZero,
    insuranceQuote,
  })

  const checkTeamLimitExceeded = ({ registeredTeam, registrations }) => {
    const { teamDetails } = registeredTeam

    if (Object.keys(teamDetails).length !== 0) {
      const { participantsPerTeamLimit, teamParticipantCount } = teamDetails
      const current = Object.keys(registrations).length

      return teamParticipantCount + current > participantsPerTeamLimit
    }
    return false
  }

  const showConfirm = (orgType, callback, name, childProducts = []) => {
    const removeType = orgType.includes('remove') ? 'remove' : orgType
    const state = {
      payment: {
        label: 'Proceed to Payment',
        showModal: true,
        description: (
          <>
            <p>Are you sure you want to proceed to payment?</p>
            <p>
              You will not be able to amend {formType?.toLowerCase() === 'shop' ? 'purchase' : 'registration'} after
              proceeding
            </p>
          </>
        ),
        okText: 'Yes, proceed',
        onModalSubmit: () => {
          callback()
        },
        id: 'confirmation-proceed',
      },
      remove: {
        label: isTeamRego ? 'Remove Team' : 'Remove Member',
        showModal: true,
        description:
          orgType === 'removeAll' ? (
            // biome-ignore lint/complexity/noUselessFragments: TODO
            <>
              <p>
                Are you sure you want to remove <b>{name}</b>?
              </p>
            </>
          ) : (
            <>
              <p>
                Removing this product will also remove the {isTeamRego ? 'team ' : 'member '}
                <b>{name}</b>
              </p>
              <p>Are you sure you want to remove it?</p>
              {!isEmpty(childProducts) && (
                <p>
                  You will be removing the product(s) <b>{childProducts.join(', ')}</b>
                </p>
              )}
            </>
          ),
        okText: 'Yes, Remove',
        onModalSubmit: () => {
          callback()
        },
        className: styles.removeModalButton,
        id: 'confirmation-remove',
      },
      products: {
        label: 'Remove Products',
        showModal: true,
        description: (
          <>
            <p>Are you sure you want to remove this product {name}?</p>
            <p>Removing this product will also remove the associated child products listed below.</p>
            <p>
              <b>{childProducts.join(', ')}</b>
            </p>
          </>
        ),

        okText: 'Yes, Remove',
        onModalSubmit: () => {
          callback()
        },
        className: styles.removeModalButton,
        id: 'confirmation-remove',
      },
    }
    onModalStateChange(state[removeType])
  }

  if (tfcError.error && summaryPageError.title !== tfcError.errorMessage) {
    onSummaryPageError({
      title: tfcError.errorMessage,
    })
  }

  const handleApplyVoucher = voucher => {
    applyVoucher(voucher, voucherResponse => {
      if (voucherResponse.success) {
        setIsVoucherEnabledForm(true)
        if (startTimer) {
          resetTimer()
        } else {
          setStartTimer(true)
        }
      }
    })
  }

  const handleRemoveVoucher = memberId => {
    const memberSummary = summaries.find(s => s.id === memberId)
    const memberVoucher = appliedVouchers[memberId]

    if (memberSummary && memberVoucher) {
      removeVoucher(memberId, newAppliedCodes => {
        if (isVoucherEnabledForm && Object.keys(newAppliedCodes).length === 0) {
          setStartTimer(false)
        }
      })
    }
  }

  const removeSubEventParent = (member, itemId, memberId) => {
    const itemsToBeRemoved = [{ memberId, itemId }]

    const mappedSubEvents = mapSubEvents(formData.subEvents)

    /* eslint-disable-next-line prefer-const */
    const allProducts = []
    formData.subEvents.forEach(s => s.products.forEach(p => allProducts.push(p.sfid)))

    // duplicate product and parent id arrays
    const duplicateProdIds = uniq(filter(allProducts, (v, i, a) => a.indexOf(v) !== i))
    const duplicateParentIds = formData.subEvents.reduce((a, s) => {
      duplicateProdIds.forEach(d => {
        s.products.forEach(p => {
          if (p.sfid === d) {
            s.relatedProducts.forEach(rp => a.push(rp.sfid))
          }
        })
      })
      return uniq(a)
    }, [])

    // member item product ids
    const memberItemIds = member.items.reduce((a, k) => {
      a.push(k.id)
      return a
    }, [])

    if (Object.keys(mappedSubEvents).includes(itemId)) {
      let hasOtherParent = false
      let newParents = duplicateParentIds
      if (duplicateParentIds.includes(itemId)) {
        newParents = xor(duplicateParentIds, [itemId])
      }

      // check if a child product still has a different parent product in the list
      newParents.forEach(np => {
        if (memberItemIds.includes(np)) {
          duplicateProdIds.forEach(dp => {
            if (mappedSubEvents[np].includes(dp)) {
              hasOtherParent = true
            }
          })
        }
      })

      // decide which items to remove from cart
      member.items.forEach(mi => {
        if (mappedSubEvents[itemId].includes(mi.id)) {
          if (hasOtherParent) {
            if (!duplicateProdIds.includes(mi.id)) {
              itemsToBeRemoved.push({ memberId, itemId: mi.id })
            }
          } else {
            itemsToBeRemoved.push({ memberId, itemId: mi.id })
          }
        }
      })

      const seProduct = member.items.filter(i => i.id === itemId)
      const childProducts = member.items
        .filter(i => i.id !== itemId)
        .reduce((a, c) => {
          itemsToBeRemoved.forEach(i => {
            if (i.itemId === c.id) {
              a.push(c.name)
            }
          })
          return a
        }, [])

      if (itemsToBeRemoved.length >= (member.items || []).length) {
        return showConfirm(
          'remove',
          () => {
            handleRemoveVoucher(memberId)
            removeMember(memberId)
          },
          member.title,
          childProducts
        )
      } else if (!isEmpty(childProducts)) {
        return showConfirm(
          'products',
          () => {
            handleRemoveVoucher(memberId)
            removeMemberItems(itemsToBeRemoved)
          },
          seProduct[0].name,
          childProducts.filter(c => c !== seProduct[0].name)
        )
      } else {
        handleRemoveVoucher(memberId)
        removeMemberItems(itemsToBeRemoved)
      }
    } else {
      /* eslint-disable-next-line no-lonely-if */
      if (itemsToBeRemoved.length >= (member.items || []).length) {
        return showConfirm(
          'remove',
          () => {
            handleRemoveVoucher(memberId)
            removeMember(memberId)
          },
          member.title
        )
      } else {
        handleRemoveVoucher(memberId)
        removeMemberItems(itemsToBeRemoved)
      }
    }
  }

  const onRemoveItem = (summaryId, itemId, summary) => {
    const itemsToBeRemoved = [{ summaryId, itemId }]

    const memberPaidProduct = paidProductsByMember[summaryId] || []
    const isCurrentPaid = memberPaidProduct.some(mp => mp.sfid === itemId)

    const summaryItems = (summary.items || []).reduce((a, i) => {
      a.push(i.sfid)
      return a
    }, [])

    if (!isCurrentPaid && childProductMap[itemId]) {
      Object.keys(childProductMap[itemId]).forEach(key => {
        if (summaryItems.includes(key)) {
          itemsToBeRemoved.push({
            summaryId,
            itemId: key,
          })
        }
      })
    }

    if (hasSubEvents && formData.subEvents) {
      removeSubEventParent(summary, itemId, summaryId)
    } else {
      if (itemsToBeRemoved.length >= (summary.items || []).length) {
        return showConfirm(
          'remove',
          () => {
            handleRemoveVoucher(summaryId)

            if (isTeamRego) {
              removeTeam()
              goto(0)
              return
            }

            removeMember(summaryId)
          },
          summary.title
        )
      }
      handleRemoveVoucher(summaryId)
      isTeamRego ? removeTeamItems(itemsToBeRemoved) : removeMemberItems(itemsToBeRemoved)
    }
  }

  const proceedToPayment = () => {
    const vErrors = []
    Object.keys(voucherDataOnLoad).forEach(a => {
      // biome-ignore lint/complexity/useOptionalChain: TODO
      if (voucherDataOnLoad[a] && voucherDataOnLoad[a].message) vErrors.push({ el: a, ...voucherDataOnLoad[a] })
    })

    if (vErrors.length > 0) {
      scrollToElement(`#voucherCode-${vErrors[0].el}`, {
        offset: -150,
        ease: 'in-out-quad',
        duration: 250,
        align: 'top',
      })
      return
    }

    setInsuranceError(false)
    if (insuranceQuote && !insuranceQuote.hasInteracted) {
      setInsuranceError(true)

      if (typeof tg.focus === 'function') {
        scrollToInsuranceWidget(insuranceProvider)
      }

      return
    }

    if (isTeamRego) {
      if (Number(paymentData.total.rawValue) === 0) {
        validateSessions({
          sessions,
          paymentData: paymentData,
          team: registrations,
          isTeamRego,
          next: () => goto(successPage),
        })
      } else {
        showConfirm('payment', () => {
          setPaymentMethod(paymentProvider)
          validateSessions({
            sessions,
            paymentData: paymentData,
            team: registrations,
            isTeamRego,
            next,
          })
        })
      }
    } else {
      const { members, ...restRegistrations } = registrations
      const validatedVoucherMembers = members.reduce((acc, m) => {
        const v = voucherDataOnLoad[m.id]
        if (v && isEqual(m.voucherCode, v.voucherCode) && isEqual(m.voucherProvider, v.provider)) {
          acc.push({ ...m, voucherValid: true })
        } else {
          acc.push(m)
        }
        return acc
      }, [])

      const validatedVoucherRegistrations = {
        members: validatedVoucherMembers,
        ...restRegistrations,
      }

      if (Number(paymentData.total.rawValue) === 0) {
        validateSessions({
          sessions,
          registrations: validatedVoucherRegistrations,
          paymentData: paymentData,
          isTeamRego,
          ...(isTeamEvent && {
            team: registeredTeam,
          }),
          insuranceQuote,
          next: () => goto(successPage),
        })
      } else {
        showConfirm('payment', () => {
          setPaymentMethod(paymentProvider)
          validateSessions({
            sessions,
            registrations: validatedVoucherRegistrations,
            paymentData: paymentData,
            isTeamRego,
            ...(isTeamEvent && {
              team: registeredTeam,
            }),
            insuranceQuote,
            next,
          })
        })
      }
    }
  }

  const handleProceedToPayment = () => {
    if (!hasAppliedVoucher) {
      proceedToPayment()
      return
    }

    validateVoucherBeforeProceed(voucherSessionId, validationSuccess => {
      if (validationSuccess) {
        proceedToPayment()
      }
    })
  }

  useEffect(() => {
    if (!tfcError.error && !isNil(summaryPageError.title)) {
      onSummaryPageError({
        title: null,
      })
    }
  }, [])

  const showVouchers = getShowVouchers(registrations)
  const showPromoCodes = !hasAppliedVoucher

  return (
    <>
      {/* biome-ignore lint/complexity/useOptionalChain: TODO */}
      {summaryPageError && summaryPageError.title && (
        <div className={styles.flashMessageContainer}>
          <FlashMessage type="error" showInterval={summaryPageError.showInterval || 0} title={summaryPageError.title}>
            {summaryPageError.description}
          </FlashMessage>
        </div>
      )}
      <h2>{isTeamRego ? 'Team Registration Summary' : 'Summary'}</h2>
      {summaries.map((summary, i) => {
        const { showVoucherInput, voucherProviders } = showVouchers[summary.id] || {}
        return (
          <StyledSummary key={summary.id} buttonColour={textLinkColour} className={styles.member}>
            <RemoveWrapper
              data={summary}
              summaryIndex={i}
              formType={formType}
              formData={formData}
              itemsGroupMap={itemsGroupMap[summary.id]}
              displayPriceAsZero={displayPriceAsZero}
              onEdit={() => {
                setCurrentRegistration(summary.id)
                isTeamEvent ? (!isTeamRego ? goto(2) : goto(0)) : goto(0)
              }}
              onRemove={summaryId => {
                showConfirm(
                  'removeAll',
                  () => {
                    handleRemoveVoucher(summaryId)

                    if (isTeamRego) {
                      removeTeam()
                      goto(0)
                      return
                    }

                    removeMember(summaryId)
                  },
                  summary.title
                )
              }}
              onRemoveItem={(summaryId, itemId) => onRemoveItem(summaryId, itemId, summary)}
              isLocked={useInstalments}
            >
              {showVoucherInput && (
                <StyledVoucher buttonColour={textLinkColour}>
                  <VoucherCode
                    memberId={summary.id}
                    voucherData={voucherDataOnLoad}
                    voucherProviders={voucherProviders}
                    appliedVoucherCodes={appliedVoucherCodes}
                    onApplyVoucher={handleApplyVoucher}
                    onRemoveVoucher={handleRemoveVoucher}
                    buttonColour={buttonColour}
                    buttonTextColour={buttonTextColour}
                    disableInputField={useInstalments}
                  />
                </StyledVoucher>
              )}
            </RemoveWrapper>
          </StyledSummary>
        )
      })}
      {!isTeamRego && (
        <StyledRegisterAnother>
          <ButtonWithLink
            onClick={onRegisterAnother || (() => (isTeamEvent ? (isTeamRego ? goto(0) : goto(1)) : goto(-1)))}
            buttoncolour={buttonColour}
            buttontextcolour={buttonTextColour}
            disabled={checkTeamLimitExceeded(formData) || useInstalments}
            label={formType.toLowerCase() === 'shop' ? 'Purchase for Another Person' : 'Register Another Person'}
            id="register-another"
          />
          {checkTeamLimitExceeded(formData) && <ErrorMessage>Maximum participants for this team reached</ErrorMessage>}
        </StyledRegisterAnother>
      )}
      {appliedBundleDiscounts && <AppliedDiscounts appliedDiscounts={appliedBundleDiscounts} />}
      {showPromoCodes && (
        <StyledPromoCode buttonColour={textLinkColour}>
          <PromoCode
            value={promoCodeValue}
            error={promoCodeError}
            appliedCodes={validatedPromoCodes}
            onAddCode={addPromoCode}
            onRemoveCode={removePromoCode}
            buttonColour={buttonColour}
            buttonTextColour={buttonTextColour}
            disableInputField={useInstalments}
          />
        </StyledPromoCode>
      )}
      {isInsuranceEnabled && (
        <StyledInsuranceBody isError={insuranceError && !insuranceQuote?.hasInteracted}>
          <InsuranceBody insuranceProvider={insuranceProvider} />
          {insuranceError && !insuranceQuote?.hasInteracted && (
            <ErrorMessage>Please select whether you would like Refund Protection</ErrorMessage>
          )}
        </StyledInsuranceBody>
      )}
      <PaymentSummary data={paymentData} displayPriceAsZero={displayPriceAsZero} />
      <PaymentGateway
        total={Number(paymentData.total.rawValue)}
        setPaymentMethod={setPaymentMethod}
        gateways={paymentGateway}
        onGatewayChange={setPaymentProvider}
        onInstalmentUse={setUseInstalments}
        buttonColour={textLinkColour}
        formSettings={formSettings}
        instalments={instalments}
      />
      <NavigationButtons
        submitLabel={
          Number(paymentData.total.rawValue) > 0
            ? 'Proceed to Payment'
            : `Submit ${formType.toLowerCase() === 'shop' ? 'Purchase' : 'Registration'}`
        }
        buttonColour={buttonColour}
        buttonTextColour={buttonTextColour}
        onSubmit={handleProceedToPayment}
        onBack={() => {
          onSummaryPageError({
            title: null,
          })
          previous()
        }}
        // biome-ignore lint/complexity/useOptionalChain: TODO
        isContinueDisabled={tfcError && tfcError.error}
        hideHr
        hideBackButton={useInstalments}
      />
      <StyledModal>
        <Modal
          saveButtonClassName={modalState.className}
          label={modalState.label}
          isShowSaveButton
          isShowBottomBar
          isOpen={modalState.showModal}
          handleModalClose={() => onModalStateChange({ ...initialModalState })}
          handleModalSubmit={() => {
            modalState.onModalSubmit()
            onModalStateChange({ ...initialModalState })
          }}
          isSubmitWithOutClose
          midWidthPopup={false}
          fullScreen={false}
          titleBottomBorder
          isError={false}
          okText={modalState.okText || 'Yes, Remove'}
          okId={modalState.id}
          isCustomButtonColour={modalState.okText !== 'Yes, Remove'}
          buttonColour={buttonColour}
          buttonTextColour={buttonTextColour}
        >
          {modalState.description}
        </Modal>
      </StyledModal>
    </>
  )
}

SummaryComponent.defaultProps = {
  summaryPageError: null,
}

export default SummaryComponent
